import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
  IconButton,
  Divider,
  Chip,
  Button,
  Stack,
  Tooltip,
  SxProps,
  Theme,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import useResponsive from '../../hooks/useResponsive';

export interface BreadcrumbItem {
  label: string;
  path?: string;
  icon?: React.ReactNode;
}

export interface TabItem {
  label: string;
  value: number | string;
  icon?: React.ReactElement;
  badge?: number;
}

export interface ActionButton {
  label: string;
  onClick: () => void;
  variant?: 'text' | 'outlined' | 'contained';
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface PageHeaderProps {
  // Title Section
  title?: string;
  subtitle?: string;
  backButton?: boolean;
  onBack?: () => void;

  // Breadcrumbs
  breadcrumbs?: BreadcrumbItem[];

  // Search Component
  searchComponent?: React.ReactNode;

  // Tabs
  tabs?: TabItem[];
  activeTab?: number | string;
  onTabChange?: (event: React.SyntheticEvent, value: number | string) => void;

  // Actions
  actions?: ActionButton[];
  menuActions?: React.ReactNode;

  // Status/Tags
  status?: {
    label: string;
    color?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  };
  tags?: string[];

  // Custom content areas
  customContent?: React.ReactNode;
  rightContent?: React.ReactNode;

  // Styling
  variant?: 'default' | 'compact' | 'search' | 'tabs';
  showDivider?: boolean;
  sx?: SxProps<Theme>;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  backButton,
  onBack,
  breadcrumbs,
  searchComponent,
  tabs,
  activeTab,
  onTabChange,
  actions,
  menuActions,
  status,
  tags,
  customContent,
  rightContent,
  variant = 'default',
  showDivider = true,
  sx,
}) => {
  const navigate = useNavigate();
  const { sm, md, lg } = useResponsive();

  // Desktop-only header (mobile gets minimal/no header to save space)
  // sm is true when screen width < sm breakpoint (mobile)
  const isMobile = sm;

  // Temporarily disable mobile check to ensure header shows
  if (false && isMobile) {
    // On mobile, only show essential elements inline with content
    if (searchComponent || tabs) {
      return (
        <Box sx={{ px: 2, py: 1, bgcolor: 'background.paper' }}>
          {searchComponent}
          {tabs && (
            <Tabs
              value={activeTab}
              onChange={onTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ minHeight: 40 }}
            >
              {tabs.map(tab => (
                <Tab
                  key={tab.value}
                  label={tab.label}
                  value={tab.value}
                  icon={tab.icon}
                  iconPosition="start"
                  sx={{ minHeight: 40, py: 1 }}
                />
              ))}
            </Tabs>
          )}
        </Box>
      );
    }
    return null;
  }

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  // Height configurations for different variants - all consistent
  const headerHeight = 64; // Match navbar height to align perfectly with horizontal line

  return (
    <Box
      sx={{
        bgcolor: 'var(--surface-raised)',
        borderBottom: 'none',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: 'var(--elevation-1)',
        ...sx,
      }}
    >
      {/* Main Header Content */}
      <Box
        sx={{
          height: headerHeight,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {/* Left Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          {/* Back Button */}
          {backButton && (
            <IconButton onClick={handleBack} size="small">
              <ArrowBackIcon />
            </IconButton>
          )}

          {/* Title Section or Search */}
          {variant === 'search' ? (
            <Box sx={{ flex: 1, maxWidth: 600 }}>{searchComponent}</Box>
          ) : (
            <Box>
              {/* Title */}
              {title && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography
                    variant="h6"
                    component="h1"
                    sx={{
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                      fontSize: '1.25rem',
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {title}
                  </Typography>

                  {/* Status Chip */}
                  {status && (
                    <Chip label={status.label} color={status.color || 'default'} size="small" sx={{ height: 24 }} />
                  )}

                  {/* Tags */}
                  {tags && tags.length > 0 && (
                    <Stack direction="row" spacing={0.5}>
                      {tags.map(tag => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.75rem' }}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              )}

              {/* Subtitle */}
              {subtitle && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
          )}

          {/* Custom Content */}
          {customContent}
        </Box>

        {/* Right Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Search in default variant */}
          {variant === 'default' && searchComponent && <Box sx={{ width: 300 }}>{searchComponent}</Box>}

          {/* Right Custom Content */}
          {rightContent}

          {/* Action Buttons */}
          {actions && actions.length > 0 && (
            <Stack direction="row" spacing={1}>
              {actions.map((action, index) => (
                <Button
                  key={index}
                  variant={action.variant || 'contained'}
                  color={action.color || 'primary'}
                  onClick={action.onClick}
                  disabled={action.disabled}
                  startIcon={action.icon}
                  size="small"
                  sx={{
                    height: 36,
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                    boxShadow: action.variant === 'contained' ? '0 1px 2px rgba(0, 0, 0, 0.08)' : 'none',
                    '&:hover': {
                      boxShadow: action.variant === 'contained' ? '0 2px 4px rgba(0, 0, 0, 0.12)' : 'none',
                    },
                  }}
                >
                  {action.label}
                </Button>
              ))}
            </Stack>
          )}

          {/* Menu Actions */}
          {menuActions && (
            <IconButton size="small">
              <MoreVertIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Tabs Section (below main header) */}
      {tabs && tabs.length > 0 && (
        <Box
          sx={{
            px: 3,
            borderTop: 'none', // Remove border for cleaner look
          }}
        >
          <Tabs
            value={activeTab}
            onChange={onTabChange}
            sx={{
              minHeight: 48,
              '& .MuiTab-root': {
                minHeight: 48,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.875rem',
                color: 'var(--text-secondary)',
                '&.Mui-selected': {
                  color: 'var(--brand-green, #3F5D66)',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'var(--brand-green, #3F5D66)',
                height: 2,
              },
            }}
          >
            {tabs.map(tab => {
              const tabLabel =
                tab.badge && tab.badge > 0 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{tab.label}</span>
                    <Chip
                      label={tab.badge}
                      size="small"
                      sx={{
                        height: 20,
                        minWidth: 20,
                        fontSize: '0.7rem',
                      }}
                    />
                  </Box>
                ) : (
                  tab.label
                );

              return <Tab key={tab.value} label={tabLabel} icon={tab.icon} iconPosition="start" value={tab.value} />;
            })}
          </Tabs>
        </Box>
      )}
    </Box>
  );
};

export default PageHeader;
